import { UI_LANGUAGES, type UILanguageName } from "@/i18n/languages";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Settings, Globe, Sun, Moon, ChevronDown } from "lucide-react";
import { useTheme, useLanguage } from "@/store/use-ui-preferences-store";

export const QuickSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Global tema ve dil store'larını kullan
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();

  const languages = UI_LANGUAGES;

  const handleToggleTheme = () => {
    toggleTheme(); // Geçici olarak eski sistem
  };

  const handleChangeLanguage = (
    langCode: UILanguageName
  ) => {
    setLanguage(langCode); // Geçici olarak eski sistem
  };
  if (import.meta.env.VITE_APP_ENV !== "development") {
    return null;
  }
  return (
    <div className="absolute bottom-6 right-6 z-[9999]">
      {/* Settings Panel */}
      {isOpen && (
        <div className="mb-4 bg-bg-primary dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl shadow-lg p-4 min-w-[200px]">
          {/* Language Selection */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-text-secondary dark:text-gray-300" />
              <span className="text-sm font-medium text-text-primary dark:text-white">
                {t("common.language")}
              </span>
            </div>
            <div className="space-y-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() =>
                    handleChangeLanguage(
                      lang.code
                    )
                  }
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    language === lang.code
                      ? "bg-primary-500 text-blue-500"
                      : "text-text-secondary dark:text-gray-300 hover:bg-bg-secondary dark:hover:bg-gray-700"
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.fullLabel}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Toggle */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isDark ? (
                <Moon className="w-4 h-4 text-text-secondary dark:text-gray-300" />
              ) : (
                <Sun className="w-4 h-4 text-text-secondary dark:text-gray-300" />
              )}
              <span className="text-sm font-medium text-text-primary dark:text-white">
                {t("common.theme")}
              </span>
            </div>
            <button
              onClick={handleToggleTheme}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm bg-bg-secondary dark:bg-gray-700 text-text-primary dark:text-white hover:bg-bg-tertiary dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <>
                  <Sun className="w-4 h-4" />
                  <span>{t("common.lightMode")}</span>
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4" />
                  <span>{t("common.darkMode")}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-primary-500 hover:bg-primary-600 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-105"
      >
        {isOpen ? (
          <ChevronDown className="w-5 h-5 bg-red-500 rounded-full" />
        ) : (
          <Settings className="w-5 h-5 bg-red-500 rounded-full" />
        )}
      </button>
    </div>
  );
};
